import useIsTouchDevice from 'hooks/useIsTouchDevice';
import { throttle } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useCamAdRunning from 'utils/useGlobalAdRunningState';

const TIMEOUT = 5000;
const useCamControls = ({
  playerEventTriggerEl,
  isScrubbing,
  persistControlsOnFirstLoad,
  camId,
  forceHideControls,
  forceHideCamHostAd,
}: {
  playerEventTriggerEl: HTMLElement | null;
  isScrubbing: boolean;
  persistControlsOnFirstLoad: boolean;
  camId: string;
  forceHideControls: boolean;
  forceHideCamHostAd: boolean;
}) => {
  const initialLoadRef = useRef(persistControlsOnFirstLoad);
  const [showCamHostAd, setShowCamHostAd] = useState(true);
  const [controlsVisible, setControlsVisible] = useState(true);

  const nonTouchUserActive = useRef(false);

  const userScrubbingRef = useRef(isScrubbing);

  const isTouchDevice = useIsTouchDevice();

  const setShowControlsTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hideControlsTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearControls = useCallback(() => {
    if (userScrubbingRef.current) {
      return;
    }

    setShowCamHostAd(false);
    setControlsVisible(nonTouchUserActive.current);
  }, []);

  const resetAndHideControlsOnTimeout = useCallback(() => {
    if (setShowControlsTimeout?.current) {
      clearTimeout(setShowControlsTimeout.current);
    }

    setShowControlsTimeout.current = setTimeout(clearControls, TIMEOUT);
  }, [clearControls]);

  const [camAdRunning] = useCamAdRunning();

  useEffect(() => {
    userScrubbingRef.current = isScrubbing;

    if (!isScrubbing && !isTouchDevice && !initialLoadRef.current) {
      resetAndHideControlsOnTimeout();
    }

    if (isScrubbing && !isTouchDevice) {
      setShowCamHostAd(true);
      setControlsVisible(true);
    }
  }, [isScrubbing, resetAndHideControlsOnTimeout, isTouchDevice, initialLoadRef]);

  const handleUserActive = useMemo(
    () =>
      throttle(
        () => {
          if (hideControlsTimeout.current) {
            clearTimeout(hideControlsTimeout.current);
          }

          nonTouchUserActive.current = true;
          setShowCamHostAd(true);
          setControlsVisible(true);

          resetAndHideControlsOnTimeout();
        },
        500,
        { leading: true, trailing: false },
      ),
    [resetAndHideControlsOnTimeout],
  );

  const hideControlsOnTimeout = useCallback(() => {
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }

    hideControlsTimeout.current = setTimeout(() => {
      nonTouchUserActive.current = false;
      setControlsVisible(false);
    }, 500);
  }, []);

  const handleUserInactive = useMemo(
    () =>
      throttle(() => {
        if (!initialLoadRef.current) {
          hideControlsOnTimeout();

          resetAndHideControlsOnTimeout();
        }
      }),
    [resetAndHideControlsOnTimeout, hideControlsOnTimeout],
  );

  const handleTouchToggle = useCallback(
    (event: TouchEvent) => {
      const target = event.target as HTMLElement;
      if (
        target?.parentElement &&
        !target.parentElement.classList.contains('video-js') &&
        !target.parentElement.classList.contains('vjs-poster')
      ) {
        resetAndHideControlsOnTimeout();
        return;
      }

      setShowCamHostAd(true);
      setControlsVisible((curr) => !curr);

      resetAndHideControlsOnTimeout();
    },
    [resetAndHideControlsOnTimeout],
  );

  useEffect(() => {
    setShowCamHostAd(true);
    setControlsVisible(true);

    if (!initialLoadRef.current) {
      resetAndHideControlsOnTimeout();
    }
    initialLoadRef.current = false;
  }, [camId, resetAndHideControlsOnTimeout]);

  useEffect(() => {
    if (playerEventTriggerEl) {
      if (!isTouchDevice) {
        playerEventTriggerEl.addEventListener('mousemove', handleUserActive);
        playerEventTriggerEl.addEventListener('mouseleave', handleUserInactive);
      } else {
        playerEventTriggerEl.addEventListener('touchstart', handleTouchToggle);
      }

      return () => {
        playerEventTriggerEl.removeEventListener('mousemove', handleUserActive);
        playerEventTriggerEl.removeEventListener('mouseleave', handleUserInactive);
        playerEventTriggerEl.removeEventListener('touchstart', handleTouchToggle);

        if (setShowControlsTimeout.current) {
          clearTimeout(setShowControlsTimeout.current);
        }
      };
    }
    return undefined;
  }, [
    resetAndHideControlsOnTimeout,
    playerEventTriggerEl,
    handleUserActive,
    handleUserInactive,
    isTouchDevice,
    handleTouchToggle,
  ]);

  const controlsVisibility = useMemo(() => {
    if (camAdRunning) {
      return {
        showCamHostAd: false,
        controlsVisible: false,
      };
    }

    return {
      showCamHostAd: showCamHostAd && !forceHideCamHostAd,
      controlsVisible: controlsVisible && !forceHideControls,
    };
  }, [showCamHostAd, controlsVisible, camAdRunning, forceHideControls, forceHideCamHostAd]);

  return controlsVisibility;
};

export default useCamControls;
